<template>
  <div>
    <div class="text-right">
        <Button type="primary" size="mini" :disabled="!checkReceiptPermission" @click="saveLR">{{$t('PROFILE_SAVE')}}</Button>
      </div>
      <div class="mt-10 table-with-add">
        <div class="add-block" v-if="!canAddNew" @click="addLR">{{ $t("PROFILE_ADD_NEW") }}</div>
          <Table
            v-if="!isLrDataLoading"
            :data="lrData"
            class="table-striped bordered mt-10"
            aria-describedby="lorryReceiptData"
            >
            <table-column
              :label="$t('TRANSPORTER_PROFILE_COMPANIES_SERVICING')"
              width="200"
            >
              <template slot-scope="scope">
                {{ getCurrentClient(scope.row.client_id) }}
              </template>
            </table-column>
            <table-column
              :label="$t('TRANSPORTER_PROFILE_PRE_FIX')"
              width="130">
              <template slot-scope="scope">
                <div v-if="lrData.length && !checkReceiptPermission">
                  {{ scope.row.prefix }}
                </div>
                <Input
                  class="form-field"
                  size="mini"
                  v-model="scope.row.prefix"
                  v-else
                  :disabled="isPreviousSeries(scope.row.type)"
                />
              </template>
            </table-column>
            <table-column
              :label="$t('TRANSPORTER_PROFILE_LR_NUMBER_FROM')"
              width="130">
                <template slot-scope="scope">
                        <div v-if="!checkReceiptPermission">{{ scope.row.from }}</div>
                        <Input
                          class="form-field"
                          size="mini"
                          type="number"
                          v-model="scope.row.from"
                          v-else
                          :disabled="
                            isPreviousSeries(scope.row.type) ||
                            disableCurrentSeriesLR(scope.row)
                          "
                        />
                      </template>
              </table-column>
              <table-column
                :label="$t('TRANSPORTER_PROFILE_LR_COUNT_TO')"
                width="130">
                  <template slot-scope="scope">
                        <div v-if="!checkReceiptPermission">
                          {{ scope.row.to }}
                        </div>
                        <Input
                          class="form-field"
                          size="mini"
                          type="number"
                          v-model.number="scope.row.to"
                          v-else
                          :disabled="isPreviousSeries(scope.row.type)"
                        />
                      </template>
              </table-column>
              <table-column
                :label="$t('TRANSPORTER_PROFILE_CURRENT')"
                width="130">
                  <template slot-scope="scope">
                    {{
                      scope.row.current ? scope.row.current : scope.row.from
                    }}
                  </template>
              </table-column>
              <table-column :label="$t('TRANSPORTER_PROFILE_TYPE')">
                <template slot-scope="scope">
                  {{ $t(getLRType(scope.row.type)) }}
                </template>
            </table-column>
            <table-column align="center">
              <template slot-scope="scope">
                <span
                  class="link-action"
                  v-if="isDeactivatable(scope.row) && checkReceiptPermission"
                  @click="deactivate(scope.row)"
                >
                  <svgicon
                    name="icon-inspection"
                    width="14"
                    height="14"
                  />
                  {{ $t("PROFILE_DEACTIVATE") }}
                </span>
              </template>
            </table-column>
          </Table>
          <div v-else class="paymentloader"></div>
      </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { LRType } from 'pando-constants';

export default {
  name: 'LorryReceipt',
  data() {
    return {
      lrData: null,
    };
  },
  props: {
    lorryReceiptData: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    this.initializeData();
  },
  computed: {
    ...mapState('profile', ['isLrLoading']),
    ...mapGetters({
      clients: 'app/clients',
    }),
    isLrDataLoading() {
      return this.isLrLoading;
    },
    windowHeight() {
      return `${window.innerHeight - 380}px`;
    },
    checkReceiptPermission() {
      return this.$hasPermission('update_profile_lorry_receipt');
    },
    canAddNew() {
      return !!this.lrData.find(lr => lr.type === LRType.NEXT_LR_SERIES);
    },
    getCurrentClient() {
      return (clientId) => {
        const currentClient = this.clients.find(
          client => client.id === clientId,
        );
        return currentClient && currentClient.name
          ? currentClient.name
          : this.clients[0].name;
      };
    },
    getLRType() {
      return key => Object.keys(LRType).find(type => LRType[type] === key);
    },
    isNextSeriesPresent() {
      return !!this.lrData.find(lr => lr.type === LRType.NEXT_LR_SERIES);
    },
    isDeactivatable() {
      return data =>
        data.type === LRType.CURRENT_LR_SERIES && this.isNextSeriesPresent;
    },
  },
  methods: {
    /**
     * Handler to initialize data onload
     */
    initializeData() {
      this.lrData = this.lorryReceiptData;
    },
    /**
     * Handler to check if the lr is of type 'previous-lr-series'
     */
    isPreviousSeries(type) {
      return type === LRType.PREVIOUS_LR_SERIES;
    },
    /**
     * Trigger handler to save lr changes
     */
    saveLR() {
      this.$emit('update-lr', this.dbFormatData());
    },
    /**
     * Add a new LR row
     */
    addLR() {
      const newData = this.prePopulate();
      this.lrData = [newData, ...this.lrData];
    },
    /**
     * Handler to disable LR of type current-series
     */
    disableCurrentSeriesLR(data) {
      if (data.current && data.type === LRType.CURRENT_LR_SERIES) {
        return true;
      }
      return false;
    },
    /**
     * Emitter handler to trigger lr deactivation
     */
    deactivate(val) {
      const data = {
        ...val,
        client_id: val.client_id || this.clients[0].id,
        lr_current: parseInt(val.current, 10) || parseInt(val.from, 10),
      };
      this.$emit('deactivate-lr', data);
    },
    /**
     * Helper Handler to generate skeletal object
     */
    prePopulate() {
      const currentData =
      this.lrData && this.lrData[this.lrData.length - 1];
      let greatest;
      if (currentData && currentData.to && currentData.current) {
        greatest =
          currentData.to > currentData.current
            ? currentData.to + 1
            : currentData.current + 1;
      }
      const prePopulatedData = {
        client_id: currentData && currentData.client_id,
        prefix: currentData && currentData.prefix,
        from: greatest,
        current_value: currentData && currentData.current_value,
        type: currentData ? LRType.NEXT_LR_SERIES : LRType.CURRENT_LR_SERIES,
      };
      return Object.assign({}, prePopulatedData);
    },
    /**
     * Handler to convert payload into db format supported data
     */
    dbFormatData() {
      const formatData = [];
      this.lrData.forEach((lr) => {
        formatData.push({
          client_id: lr.client_id || this.clients[0].id,
          lr_prefix: lr.prefix || '-',
          lr_from: parseInt(lr.from, 10),
          lr_to: parseInt(lr.to, 10),
          lr_current: parseInt(lr.current, 10) || parseInt(lr.from, 10),
          lr_type: lr.type,
        });
      });
      return formatData;
    },
  },
};
</script>
